import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import api from '../utils/api';
import ModalShell from './ModalShell';

export default function RescheduleAppointmentModal({ appointment, onClose, onSaved }) {
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;

    async function fetchSlots() {
      setLoading(true);
      setError('');

      try {
        const { data } = await api.get(`/appointments/${appointment.id}/available-slots`);
        if (!mounted) return;
        setSlots(Array.isArray(data.slots) ? data.slots : []);
      } catch (err) {
        if (!mounted) return;
        setError(err.response?.data?.error || 'Nie udało się pobrać wolnych terminów.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchSlots();
    return () => {
      mounted = false;
    };
  }, [appointment.id]);

  const groupedSlots = useMemo(() => {
    const groups = {};
    for (const slot of slots) {
      const key = format(new Date(slot.start), 'yyyy-MM-dd');
      if (!groups[key]) groups[key] = [];
      groups[key].push(slot);
    }
    return Object.entries(groups);
  }, [slots]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!selectedSlot) {
      setError('Wybierz nowy wolny termin.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const { data } = await api.put(`/appointments/${appointment.id}`, {
        scheduled_at: selectedSlot,
      });
      onSaved(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Nie udało się przenieść wizyty.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title="Przenieś wizytę" onClose={onClose}>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="reschedule-summary">
          <div className="reschedule-title">{appointment.client_name || 'Klientka'}</div>
          <div className="reschedule-meta">
            {appointment.service_name} · {appointment.duration_minutes} min
          </div>
          <div className="reschedule-meta">
            Obecny termin: {format(new Date(appointment.scheduled_at), 'd MMMM yyyy, HH:mm', { locale: pl })}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Nowy termin</label>
            {loading && <div className="loading-text">Szukam wolnych slotów...</div>}

            {!loading && groupedSlots.length === 0 && (
              <div className="empty-text">Brak wolnych terminów w najbliższych dniach.</div>
            )}

            {!loading && groupedSlots.length > 0 && (
              <div className="slot-groups reschedule-slot-groups">
                {groupedSlots.map(([dayKey, daySlots]) => (
                  <div key={dayKey} className="slot-day-group">
                    <div className="slot-day-label">
                      {format(new Date(`${dayKey}T00:00:00`), 'EEEE, d MMMM', { locale: pl })}
                    </div>
                    <div className="slot-row">
                      {daySlots.map((slot) => {
                        const active = selectedSlot === slot.start;
                        return (
                          <button
                            key={slot.start}
                            type="button"
                            className={`slot-btn ${active ? 'slot-btn--selected' : ''}`}
                            onClick={() => setSelectedSlot(slot.start)}
                          >
                            {format(new Date(slot.start), 'HH:mm')}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-ghost" onClick={onClose}>Anuluj</button>
            <button type="submit" className="btn-primary" disabled={saving || loading || !selectedSlot}>
              {saving ? 'Zmieniam...' : 'Zmień termin'}
            </button>
          </div>
        </form>
    </ModalShell>
  );
}